import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api";
import SEO from "../components/SEO";

export default function B2BApplyPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [b2bStatus, setB2bStatus] = useState(null); // null=loading
  const [b2bData, setB2bData] = useState(null);
  const [form, setForm] = useState({
    company_name: "",
    business_number: "",
    representative: "",
    business_doc_url: "",
    purpose: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [docPreview, setDocPreview] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/api/b2b/status");
      setB2bStatus(data.status);
      setB2bData(data);
      if (data.status === "approved") {
        navigate("/b2b/store", { replace: true });
      }
    } catch {
      setB2bStatus("none");
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    fetchStatus();
  }, [user, fetchStatus]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/api/b2b/upload-doc", fd);
      setForm((f) => ({ ...f, business_doc_url: data.url }));
      setDocPreview(URL.createObjectURL(file));
    } catch (err) {
      setError(err.response?.data?.error || "업로드 실패");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.business_number || !form.representative || !form.business_doc_url || !form.purpose) {
      setError("필수 항목을 모두 입력해주세요");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/api/b2b/apply", form);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || "신청 실패");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="dept-access-page">
        <SEO title="사업자몰 - 일광오피스" description="B2B 사업자 전용 구매몰" path="/b2b" />
        <div className="dept-access-page__inner">
          <div className="dept-access-page__mark" aria-hidden="true">B2B</div>
          <div className="dept-access-page__copy">
            <span className="dept-access-page__eyebrow">일광오피스 사업자 서비스</span>
            <h1>사업자 전용몰</h1>
            <p>사업자몰 이용을 위해 먼저 로그인해주세요.</p>
            <div className="dept-access-page__actions">
              <Link to="/login" state={{ from: "/b2b" }} className="dept-access-page__primary">
                로그인
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (b2bStatus === null) {
    return (
      <div className="dept-access-page" aria-live="polite">
        <div className="dept-access-page__inner">
          <div className="dept-access-page__mark" aria-hidden="true">B2B</div>
          <div className="dept-access-page__copy">
            <span className="dept-access-page__eyebrow">사업자 서비스</span>
            <h1>이용 정보를 확인하고 있습니다</h1>
          </div>
        </div>
      </div>
    );
  }

  // 심사 중
  if (b2bStatus === "pending") {
    return (
      <div className="dept-access-page">
        <SEO title="사업자몰 심사중 - 일광오피스" path="/b2b" />
        <div className="dept-access-page__inner">
          <div className="dept-access-page__mark" aria-hidden="true">대기</div>
          <div className="dept-access-page__copy">
            <span className="dept-access-page__eyebrow">사업자 서비스</span>
            <h1>심사 중입니다</h1>
            <p>사업자몰 가입 신청이 접수되었습니다. 관리자 승인까지 영업일 기준 1~2일이 소요됩니다.</p>
            <dl className="mt-8 grid gap-2 text-[15px]">
              <div><dt className="inline text-gray-500">회사명</dt><dd className="inline ml-4 font-bold text-gray-900">{b2bData?.company_name}</dd></div>
              <div><dt className="inline text-gray-500">사업자번호</dt><dd className="inline ml-4 font-bold text-gray-900">{b2bData?.business_number}</dd></div>
              <div><dt className="inline text-gray-500">대표자명</dt><dd className="inline ml-4 font-bold text-gray-900">{b2bData?.representative}</dd></div>
            </dl>
          </div>
        </div>
      </div>
    );
  }

  // 정지
  if (b2bStatus === "suspended") {
    return (
      <div className="dept-access-page">
        <SEO title="사업자몰 이용 정지 - 일광오피스" path="/b2b" />
        <div className="dept-access-page__inner">
          <div className="dept-access-page__mark" aria-hidden="true">안내</div>
          <div className="dept-access-page__copy">
            <span className="dept-access-page__eyebrow">사업자 서비스</span>
            <h1>이용이 정지되었습니다</h1>
            <p>사업자몰 이용이 일시 정지되었습니다. 고객센터 1600-7542로 문의해주세요.</p>
            <div className="dept-access-page__actions">
              <a href="tel:16007542" className="dept-access-page__primary">1600-7542 전화 상담</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 거절 → 재신청 또는 최초 신청 폼
  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <SEO title="사업자몰 신청 - 일광오피스" description="B2B 사업자 전용 구매몰 신청" path="/b2b" />

      <div className="max-w-xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21"/></svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">사업자 전용몰 신청</h1>
          <p className="text-gray-500 mt-2">사업자등록증을 첨부하여 신청하면 관리자 승인 후 B2B 전용 가격으로 구매하실 수 있습니다.</p>
        </div>

        {/* 거절 안내 */}
        {b2bStatus === "rejected" && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-5 mb-6">
            <p className="text-red-800 font-semibold text-sm mb-1">이전 신청이 반려되었습니다</p>
            {b2bData?.rejected_reason && (
              <p className="text-red-600 text-sm">사유: {b2bData.rejected_reason}</p>
            )}
            <p className="text-red-500 text-xs mt-2">아래 양식에서 수정하여 다시 신청해주세요.</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">회사명 *</label>
            <input type="text" value={form.company_name}
              onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[15px]"
              placeholder="회사명을 입력해주세요" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">사업자등록번호 *</label>
            <input type="text" value={form.business_number}
              onChange={(e) => setForm((f) => ({ ...f, business_number: e.target.value }))}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[15px]"
              placeholder="000-00-00000" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">대표자명 *</label>
            <input type="text" value={form.representative}
              onChange={(e) => setForm((f) => ({ ...f, representative: e.target.value }))}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[15px]"
              placeholder="대표자 이름" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">사업자등록증 *</label>
            <div className="relative">
              {docPreview || form.business_doc_url ? (
                <div className="relative group">
                  <img src={docPreview || form.business_doc_url} alt="사업자등록증"
                    className="w-full max-h-64 object-contain bg-gray-50 rounded-xl border border-gray-200" />
                  <button type="button"
                    onClick={() => { setDocPreview(""); setForm((f) => ({ ...f, business_doc_url: "" })); }}
                    className="absolute top-2 right-2 bg-black/60 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition">
                    ✕
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-40 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition">
                  <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/></svg>
                  <span className="text-sm text-gray-500">
                    {uploading ? "업로드 중..." : "클릭하여 사업자등록증 업로드"}
                  </span>
                  <input type="file" className="hidden" accept="image/*" onChange={handleUpload} disabled={uploading} />
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">가입 목적 *</label>
            <div className="grid grid-cols-1 gap-2">
              {[
                { value: "제품 구매", label: "사업자몰 제품 구매" },
                { value: "입점", label: "사업자몰 입점" },
                { value: "구매 및 판매", label: "사업자몰 구매 및 판매" },
              ].map((opt) => (
                <button key={opt.value} type="button"
                  onClick={() => setForm((f) => ({ ...f, purpose: opt.value }))}
                  className={`w-full px-4 py-3.5 rounded-xl text-[15px] font-medium transition text-left ${
                    form.purpose === opt.value
                      ? "bg-[#0071e3] text-white ring-2 ring-[#0071e3] ring-offset-2"
                      : "bg-gray-50 text-gray-700 border border-gray-200 hover:border-blue-300 hover:bg-blue-50/30"
                  }`}>
                  <span className="flex items-center gap-3">
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      form.purpose === opt.value ? "border-white" : "border-gray-300"
                    }`}>
                      {form.purpose === opt.value && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
                    </span>
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <button type="submit" disabled={submitting || uploading}
            className="w-full py-3.5 bg-[#0071e3] text-white rounded-xl font-medium hover:bg-[#0077ed] transition disabled:opacity-50 disabled:cursor-not-allowed text-[15px]">
            {submitting ? "신청 중..." : b2bStatus === "rejected" ? "다시 신청하기" : "사업자몰 신청"}
          </button>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          사업자등록증 확인 후 영업일 기준 1~2일 이내 승인됩니다. 문의: 1600-7542
        </p>
      </div>
    </div>
  );
}
